import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { fdiDetailData } from '../../../data/fdiData';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { getTooltipStyle } from '../../../hooks/useExpandedStyles';
import { Globe, ArrowUpRight } from 'lucide-react';

const COLORS = ['#00d26a', '#00a3ff', '#ffb800', '#a855f7', '#ec4899', '#06b6d4', '#30363d'];

interface SourceCountriesPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
  isExpanded?: boolean;
}

export const SourceCountriesPanel: React.FC<SourceCountriesPanelProps> = ({
  isMinimized,
  onMinimize,
  isExpanded = false,
}) => {
  const { sourceCountries } = fdiDetailData;

  const top5 = sourceCountries.slice(0, 5);
  const otherShare = sourceCountries.slice(5).reduce((sum, c) => sum + c.share, 0);
  const donutData = [...top5.map((c) => ({ name: c.code, value: c.share })), { name: 'Other', value: otherShare }];

  // Prepare bar chart data for expanded view
  const barChartData = sourceCountries.slice(0, 10).map((c) => ({
    name: c.country || c.code,
    code: c.code,
    value: c.value,
    share: c.share,
    yoy: c.yoy,
    flag: c.flag,
  }));

  const expandedContent = (
    <div className="flex flex-col h-full gap-4 md:gap-8">
      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Source Countries</div>
          <div className="text-xl md:text-3xl font-bold text-white">47</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Active investors in 2024</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Top 5 Share</div>
          <div className="text-xl md:text-3xl font-bold text-data-green">{(100 - otherShare).toFixed(0)}%</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Of total FDI inflows</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Fastest Growth</div>
          <div className="text-xl md:text-3xl font-bold text-data-amber">+115%</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Saudi Arabia YoY</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">New Entrants</div>
          <div className="text-xl md:text-3xl font-bold text-data-green">5</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">First-time investors 2024</div>
        </div>
      </div>

      {/* Main Content - Charts Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Donut Chart - Larger */}
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <div className="flex items-center gap-2 mb-3 md:mb-4">
            <Globe size={16} className="text-gray-500 md:w-[18px] md:h-[18px]" />
            <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide">
              Source Distribution
            </h3>
          </div>
          <div className="h-[200px] md:h-[300px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="35%"
                  outerRadius="60%"
                  paddingAngle={2}
                >
                  {donutData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-xs md:text-sm text-gray-500">TOP 5</div>
                <div className="text-lg md:text-2xl font-bold text-white">{(100 - otherShare).toFixed(0)}%</div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-4 mt-3 md:mt-4">
            {donutData.map((entry, i) => (
              <div key={entry.name} className="flex items-center gap-1 md:gap-2">
                <span className="w-2 h-2 md:w-3 md:h-3 rounded" style={{ backgroundColor: COLORS[i] }} />
                <span className="text-xs md:text-sm text-gray-400">
                  {entry.name} ({entry.value.toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bar Chart - Top Sources by Value */}
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
            Investment by Country (USD Billions)
          </h3>
          <div className="h-[250px] md:h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={barChartData.slice(0, 8)}
                layout="vertical"
                margin={{ top: 10, right: 20, bottom: 10, left: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e26" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  tickFormatter={(v) => `$${v}B`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  width={55}
                />
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`$${value.toFixed(2)}B`, 'Investment']}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {barChartData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-terminal-bg rounded-lg p-3 md:p-6">
        <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
          Complete Source Country Breakdown
        </h3>
        <div className="overflow-auto -mx-3 px-3 md:mx-0 md:px-0">
          <table className="w-full text-xs md:text-sm min-w-[500px]">
            <thead>
              <tr className="text-gray-500 uppercase text-xs md:text-sm tracking-wide border-b border-terminal-border">
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium">Rank</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium">Country</th>
                <th className="text-right py-2 md:py-3 px-2 md:px-4 font-medium">Value</th>
                <th className="text-right py-2 md:py-3 px-2 md:px-4 font-medium">Share</th>
                <th className="text-right py-2 md:py-3 px-2 md:px-4 font-medium">YoY</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium hidden sm:table-cell">Distribution</th>
              </tr>
            </thead>
            <tbody>
              {sourceCountries.slice(0, 12).map((c, index) => (
                <tr
                  key={c.code}
                  className="border-b border-terminal-border/30 hover:bg-terminal-panel/50 transition-colors"
                >
                  <td className="py-2 md:py-3 px-2 md:px-4 text-gray-500">#{index + 1}</td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-white">
                    <span className="mr-1 md:mr-2">{c.flag}</span>
                    <span className="hidden sm:inline">{c.country || c.code}</span>
                    <span className="sm:hidden">{c.code}</span>
                  </td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-right text-white font-semibold">${c.value.toFixed(2)}B</td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-right text-gray-400">{c.share}%</td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-right">
                    <span
                      className={`flex items-center justify-end gap-0.5 md:gap-1 ${
                        c.yoy > 0 ? 'text-data-green' : 'text-data-red'
                      }`}
                    >
                      <ArrowUpRight size={12} className={`md:w-[14px] md:h-[14px] ${c.yoy < 0 ? 'rotate-180' : ''}`} />
                      {c.yoy > 0 ? '+' : ''}
                      {c.yoy}%
                    </span>
                  </td>
                  <td className="py-2 md:py-3 px-2 md:px-4 hidden sm:table-cell">
                    <div className="w-16 md:w-24 h-1.5 md:h-2 bg-terminal-border rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${c.share}%`,
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Regional Insights */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4">
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-green">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Asia-Pacific</div>
          <div className="text-xl md:text-2xl font-bold text-white">45%</div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">China, Korea, Japan lead</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-amber">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Europe & CIS</div>
          <div className="text-xl md:text-2xl font-bold text-white">35%</div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">Russia, Turkey, Germany</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-blue">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Middle East</div>
          <div className="text-xl md:text-2xl font-bold text-white">15%</div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">Saudi, UAE fast growing</div>
        </div>
      </div>
    </div>
  );

  const compactContent = (
    <div className="flex flex-col h-full p-3">
      {/* Header Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
          <div className="text-2xl font-mono font-bold text-data-green">{(100 - otherShare).toFixed(0)}%</div>
          <div className="text-xs text-gray-500">Top 5 Share</div>
        </div>
        <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
          <div className="text-2xl font-mono font-bold text-white">47</div>
          <div className="text-xs text-gray-500">Source Countries</div>
        </div>
      </div>

      {/* Country List with Flags */}
      <div className="flex-1 space-y-2.5">
        {sourceCountries.slice(0, 6).map((c, idx) => (
          <div
            key={c.code}
            className="bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">{c.flag}</span>
                <span className="text-sm font-semibold text-white">{c.country || c.code}</span>
                {idx === 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-data-green/20 text-data-green font-semibold">
                    #1
                  </span>
                )}
              </div>
              <span className="text-lg font-mono font-bold text-data-green">
                {c.share}%
              </span>
            </div>

            {/* Investment Bar */}
            <div className="h-2 bg-terminal-border rounded-full overflow-hidden mb-2">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(c.share / sourceCountries[0].share) * 100}%`,
                  backgroundColor: COLORS[idx % COLORS.length]
                }}
              />
            </div>

            {/* Value and YoY */}
            <div className="flex items-center justify-between text-xs">
              <span className="font-mono text-white">${c.value.toFixed(2)}B</span>
              <span className={`flex items-center gap-1 ${c.yoy > 80 ? 'text-data-amber' : 'text-data-green'}`}>
                <ArrowUpRight size={12} />
                +{c.yoy}%
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );

  if (isExpanded) {
    return expandedContent;
  }

  return (
    <Panel
      title="SOURCE COUNTRIES"
      subtitle="TOP 10"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      expandedContent={expandedContent}
    >
      {compactContent}
    </Panel>
  );
};

export default SourceCountriesPanel;
